/**
 * BrandingLogoUpload (Story 14.5 — AC #11; parameterized for favicon in 15.2)
 *
 * Tenant asset uploader for the `/settings/branding` page. Mirrors AvatarUpload
 * but is tenant-scoped. One component, two assets (`asset="logo" | "favicon"`):
 *   1. Validate file type + size (+ min-dimension for raster logos) client-side.
 *   2. Request presigned R2 URL (logo or favicon endpoint).
 *   3. PUT the file directly to R2.
 *   4. Call onUploadComplete(newUrl) — PARENT owns the PATCH mutation.
 *
 * SVG security: preview always renders via `<img src>` — never inline `<svg>`
 * or `dangerouslySetInnerHTML` (Story 14.5 AC #4 + D38).
 */

import { useState, useRef, useEffect } from "react";
import { cn } from "@provenleap/ui";
import {
  requestLogoUploadUrl,
  requestFaviconUploadUrl,
} from "../../lib/tenantBrandingApi";
import { LoadingSpinner } from "../icons";

type Asset = "logo" | "favicon";

interface AssetConfig {
  allowedTypes: readonly string[];
  accept: string;
  maxBytes: number;
  maxLabel: string;
  /** Min pixel dimensions enforced for RASTER uploads (SVG exempt); null = none. */
  minDimensions: { width: number; height: number } | null;
  hint: string;
  ariaLabel: string;
  inputLabel: string;
  previewClass: string;
  request: (p: { contentType: string; fileSize: number }) => Promise<{
    uploadUrl: string;
    logoUrl?: string;
    faviconUrl?: string;
  }>;
}

const ASSET_CONFIG: Record<Asset, AssetConfig> = {
  logo: {
    allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/svg+xml"],
    accept: "image/jpeg,image/png,image/webp,image/svg+xml",
    maxBytes: 2 * 1024 * 1024,
    maxLabel: "2MB",
    minDimensions: { width: 200, height: 50 },
    hint: "SVG recommended. Min 200×50px. Max 2MB. JPG, PNG, WebP also supported.",
    ariaLabel: "Upload agency logo",
    inputLabel: "Choose agency logo file",
    previewClass: "h-16 w-auto max-w-[240px]",
    request: requestLogoUploadUrl,
  },
  favicon: {
    // Both ICO MIME spellings — browsers report .ico as image/vnd.microsoft.icon.
    allowedTypes: [
      "image/png",
      "image/svg+xml",
      "image/x-icon",
      "image/vnd.microsoft.icon",
    ],
    accept: "image/png,image/svg+xml,image/x-icon,.ico",
    maxBytes: 1 * 1024 * 1024,
    maxLabel: "1MB",
    minDimensions: null,
    hint: "Square recommended (e.g. 64×64). Max 1MB. PNG, SVG, ICO.",
    ariaLabel: "Upload favicon",
    inputLabel: "Choose favicon file",
    previewClass: "h-12 w-12",
    request: requestFaviconUploadUrl,
  },
};

/**
 * Best-effort raster min-dimension check. Returns true if OK (or undecidable —
 * we never block when the browser can't decode, or for SVG which has no
 * intrinsic pixel size). Server cannot enforce this (R2 HEAD has no dimensions),
 * so this is UX-grade validation only.
 */
async function rasterMeetsMinDimensions(
  file: File,
  min: { width: number; height: number },
): Promise<boolean> {
  if (file.type === "image/svg+xml") return true; // vector — no intrinsic px
  if (typeof createImageBitmap !== "function") return true; // can't decode → don't block
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    return bitmap.width >= min.width && bitmap.height >= min.height;
  } catch {
    return true; // decode failed → don't block the upload on a measurement error
  } finally {
    bitmap?.close?.(); // always release the decoded bitmap
  }
}

export interface BrandingLogoUploadProps {
  currentUrl: string | null;
  onUploadComplete: (newUrl: string | null) => void;
  onError: (message: string) => void;
  /** Which tenant asset this uploader manages. Defaults to "logo". */
  asset?: Asset;
  disabled?: boolean;
  className?: string;
}

export function BrandingLogoUpload({
  currentUrl,
  onUploadComplete,
  onError,
  asset = "logo",
  disabled,
  className,
}: BrandingLogoUploadProps) {
  const config = ASSET_CONFIG[asset];
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  async function handleFileSelect(file: File) {
    if (!config.allowedTypes.includes(file.type)) {
      onError(
        asset === "favicon"
          ? "Please upload a PNG, SVG, or ICO image."
          : "Please upload a JPEG, PNG, WebP, or SVG image.",
      );
      return;
    }
    if (file.size > config.maxBytes) {
      onError(`Image must be less than ${config.maxLabel}.`);
      return;
    }
    if (
      config.minDimensions &&
      !(await rasterMeetsMinDimensions(file, config.minDimensions))
    ) {
      const noun = asset === "favicon" ? "Favicon" : "Logo";
      onError(
        `${noun} must be at least ${config.minDimensions.width}×${config.minDimensions.height}px.`,
      );
      return;
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const previewUrl = URL.createObjectURL(file);
    previewUrlRef.current = previewUrl;
    setPreview(previewUrl);
    setIsUploading(true);

    try {
      const result = await config.request({
        contentType: file.type,
        fileSize: file.size,
      });
      const uploadUrl = result.uploadUrl;
      const newUrl = result.logoUrl ?? result.faviconUrl;
      if (!newUrl) throw new Error("Upload URL response missing asset URL.");

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload ${asset} to storage.`);
      }

      // Parent owns the PATCH — we only report the new R2 URL.
      onUploadComplete(newUrl);
    } catch (error) {
      setPreview(null);
      onError(
        error instanceof Error
          ? error.message
          : `Failed to upload ${asset}. Please try again.`,
      );
    } finally {
      setIsUploading(false);
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFileSelect(file);
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) void handleFileSelect(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleClick() {
    if (!disabled) fileInputRef.current?.click();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  }

  function handleRemove() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    onUploadComplete(null);
  }

  const displayUrl = preview || currentUrl;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={config.ariaLabel}
        aria-disabled={disabled || isUploading}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          "relative flex items-center justify-center rounded-2xl border-[0.5px] border-dashed border-[var(--hairline)] bg-background px-4 py-6 transition-colors",
          !disabled && "cursor-pointer hover:border-[hsl(214_25%_50%)]",
          isDragging && "ring-2 ring-primary",
          disabled && "opacity-60 cursor-not-allowed",
        )}
      >
        {displayUrl ? (
          // Security (Story 14.5 AC #4 + CVE-2026-33172 pattern): SVG branding
          // assets MUST render via <img src>, never <object>/<iframe>/<embed>/inlined.
          <img
            src={displayUrl}
            alt={`${asset === "favicon" ? "Favicon" : "Agency logo"} preview`}
            className={cn("object-contain", config.previewClass)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex flex-col items-center text-center">
            <p className="text-[13px] text-foreground">
              Click to upload or drag and drop
            </p>
            <p className="text-[12px] text-muted-foreground mt-1">
              {config.hint}
            </p>
          </div>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-background/80">
            <LoadingSpinner className="h-6 w-6" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={config.accept}
        onChange={handleInputChange}
        className="sr-only"
        aria-label={config.inputLabel}
        disabled={disabled}
      />

      {displayUrl ? (
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled || isUploading}
          className="self-start text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          Remove {asset}
        </button>
      ) : null}
    </div>
  );
}
